import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import FileUploader from 'framework/FileUploader';
import { S3Buckets } from 'app/models/S3Buckets';

interface FilePreview {
  file: File;
  base64: string;
}

const acceptedFileExtensions = ['.gif', '.jpg', '.jpeg', '.png', '.bmp'];
const maxFileSizeInMB = 10;

@Component({
  selector: 'app-components-specialist-workblockfilepicker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './workblock-file-picker.component.html',
  styleUrls: ['./workblock-file-picker.component.css']
})
export class WorkblockFilePickerComponent {
  @Input() maxFiles = 3;
  @Input() customerId: number;
  @Input() taskId: number;

  files: FilePreview[] = [];
  acceptedExtensions = acceptedFileExtensions;

  private fileUploader = inject(FileUploader);
  private validExtensionRegex = new RegExp(`\\.(${acceptedFileExtensions.map(x => x.replace('.', '')).join('|')})$`, 'i');

  get canAddMore(): boolean {
    return this.files.length < this.maxFiles;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files ?? []);
    input.value = '';

    const availableSlots = this.maxFiles - this.files.length;
    let acceptedCount = 0;

    for (const file of selectedFiles) {
      if (acceptedCount >= availableSlots) {
        toastr.error(`You may only attach up to ${this.maxFiles} files.`);
        break;
      }

      if (!this.validExtensionRegex.test(file.name)) {
        toastr.error(`Invalid file type. Only ${this.acceptedExtensions.join(', ')} are allowed.`);
        continue;
      }

      if ((file.size / 1024 / 1024) > maxFileSizeInMB) {
        toastr.error(`File size must be less than ${maxFileSizeInMB}MB.`);
        continue;
      }

      acceptedCount++;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.files.push({ file, base64: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
  }

  reset(): void {
    this.files = [];
  }

  uploadAll(): Observable<string[]> {
    if (this.files.length === 0) {
      return of([]);
    }

    const folder = `workblock/${this.customerId}/${this.taskId}`;
    const uploads = this.files.map(fileToUpload =>
      from(this.fileUploader.upload(fileToUpload.file, folder, fileToUpload.file.name, S3Buckets.s3staticfiles))
        .pipe(map(preSignedUrl => preSignedUrl.FullUrl)));

    return forkJoin(uploads);
  }
}
